import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import RefreshCCW01Svg from "@redotech/redo-icons/refresh-ccw-01_filled.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useInfiniteScroll } from "../hooks/use-infinite-scroll";
import {
  ConversationInfoCard,
  ConversationInfoCardEmpty,
  ConversationInfoCardSkeleton,
} from "./conversation-info-card";

export interface ConversationCardListProps {
  conversations: ConversationListItemResponse[];
  isLoading: boolean;
  isFetchingMore: boolean;
  isRefreshing: boolean;
  hasMore: boolean;
  onCardClick: (conversation: ConversationListItemResponse) => void;
  refresh: () => void;
  onLoadMore: () => void;
  isCardSelected: (conversation: ConversationListItemResponse) => boolean;
}

export const ConversationCardList = memo(function ConversationCardList({
  conversations,
  isLoading,
  isFetchingMore,
  isRefreshing,
  hasMore,
  onCardClick,
  refresh,
  onLoadMore,
  isCardSelected,
}: ConversationCardListProps) {
  const { scrollAreaRef, sentinelRef } = useInfiniteScroll({
    hasMore,
    isFetchingMore,
    onLoadMore,
  });

  return (
    <Flex bgColor="primary" dir="column" height="full">
      {/* Header */}
      <Flex
        align="center"
        borderBottomWidth="1px"
        borderColor="secondary"
        borderStyle="solid"
        justify="space-between"
        pl="xl"
        pr="xl"
        shrink={0}
        style={{ height: 48 }}
      >
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          Tickets
        </Text>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={RefreshCCW01Svg}
          onClick={refresh}
          pending={isRefreshing}
          size="xs"
        />
      </Flex>

      {/* Card list */}
      <Flex
        dir="column"
        flex={1}
        overflowY="auto"
        ref={scrollAreaRef}
        style={{ minHeight: 0 }}
      >
        {isLoading ? (
          <ConversationInfoCardSkeleton count={5} />
        ) : conversations.length === 0 ? (
          <ConversationInfoCardEmpty />
        ) : (
          <>
            {conversations.map((conversation) => (
              <ConversationInfoCard
                conversation={conversation}
                isActive={isCardSelected(conversation)}
                key={conversation.id}
                onClick={() => onCardClick(conversation)}
              />
            ))}
            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} style={{ height: 1 }} />
            {isFetchingMore && <ConversationInfoCardSkeleton count={2} />}
          </>
        )}
      </Flex>
    </Flex>
  );
});
